import { Dayjs } from 'dayjs';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { ClobeBankAccount } from '~models/bank/bank';
import {
  BankAccountCashFlowOverview,
  BankAccountCashFlowRequest,
  BankAccountSummaryByBusinessEntityResponse,
  BankAccountSummaryByCategoryResponse,
  UpdateBankAccountsOrderRequest,
} from '~models/cashFlow';
import { ResponseBaseWithData } from '~types/response';

interface CompanyIdentifier {
  companyId: string;
}

interface BankAccountIdentifier extends CompanyIdentifier {
  bankAccountId: number;
}

export const getBankAccountCashFlowOverview = ({
  companyId,
  request,
}: CompanyIdentifier & { request: BankAccountCashFlowRequest }) => {
  return API.get<ResponseBaseWithData<BankAccountCashFlowOverview>>(
    `/api/v3/companies/${companyId}/cashflow-overview?newOrder=true`,
    { params: request },
  );
};

interface ClobeBankAccountIdentifier extends CompanyIdentifier {
  baseDate: Dayjs;
  forLabeling?: boolean;
}
export const getClobeBankAccounts = ({
  companyId,
  baseDate,
  forLabeling,
}: ClobeBankAccountIdentifier) => {
  const queryString = QueryString.stringify(
    {
      baseDate: baseDate.format('YYYY-MM-DD'),
      forLabeling: forLabeling ?? null,
    },
    { skipNulls: true },
  );
  return API.get<ResponseBaseWithData<{ items: ClobeBankAccount[] }>>(
    `/api/v3/companies/${companyId}/bank-accounts?${queryString}`,
  );
};

export const purgeBankAccount = ({ companyId, bankAccountId }: BankAccountIdentifier) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/bank-accounts/${bankAccountId}/purge`,
  );
};

export const dePurgeBankAccount = ({ companyId, bankAccountId }: BankAccountIdentifier) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/bank-accounts/${bankAccountId}/depurge`,
  );
};

export const purgeSecurityBankAccount = ({ companyId, bankAccountId }: BankAccountIdentifier) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/security-accounts/${bankAccountId}/purge`,
  );
};

export const dePurgeSecurityBankAccount = ({ companyId, bankAccountId }: BankAccountIdentifier) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/security-accounts/${bankAccountId}/depurge`,
  );
};

export const patchChangeBankAccountAlias = ({
  companyId,
  bankAccountId,
  aliasName,
}: BankAccountIdentifier & { aliasName: string }) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/bank-accounts/${bankAccountId}/alias-name`,
    { data: { aliasName } },
  );
};

export const updateSecurityBankAccountPassword = ({
  companyId,
  securityAccountId,
  password,
}: CompanyIdentifier & { securityAccountId: number; password: string }) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/security-accounts/${securityAccountId}/password`,
    { data: { password } },
  );
};

export const patchChangeSecurityBankAccountAlias = ({
  companyId,
  bankAccountId,
  aliasName,
}: BankAccountIdentifier & { aliasName: string }) => {
  return API.patch<ResponseBaseWithData<Record<string, never>>>(
    `/api/v3/companies/${companyId}/security-accounts/${bankAccountId}/alias-name`,
    { data: { aliasName } },
  );
};

export const getBankAccountSummaryByCategory = ({
  companyId,
  request,
}: CompanyIdentifier & { request: BankAccountCashFlowRequest }) => {
  return API.get<ResponseBaseWithData<BankAccountSummaryByCategoryResponse>>(
    `/api/v3/companies/${companyId}/cashflow-overview/summary-by-category`,
    { params: request },
  );
};

export const getBankAccountSummaryByBusinessEntity = ({
  companyId,
  request,
}: CompanyIdentifier & { request: BankAccountCashFlowRequest }) => {
  return API.get<ResponseBaseWithData<BankAccountSummaryByBusinessEntityResponse>>(
    `/api/v3/companies/${companyId}/cashflow-overview/summary-by-business-entity`,
    { params: request },
  );
};

export const updateBankAccountOrder = ({
  companyId,
  request,
}: CompanyIdentifier & { request: UpdateBankAccountsOrderRequest }) => {
  return API.put(`/api/v3/companies/${companyId}/cashflow-overview/display-setting`, {
    data: request,
  });
};
